import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import {
    type SelectBoxMappedSelectionConstantType,
    type SelectBoxPreparedDataConstantType,
    type SelectBoxSelectionConstantType,
    type SelectBoxDynamicStyleType,
    type SelectBoxTypeConstantType,
    type SelectBoxType,
    type SelectedItem
} from "./type";
import type {
    Mutable
} from "../../types";
import type {
    FieldType
} from "../fieldControl/type";
import {
    webStyle
} from "../../utils";

export const SELECT_BOX_TYPES: Record<SelectBoxType, FieldType> = {
    question: "neutral",
    default: "neutral",
    neutral: "neutral",
    success: "success",
    warning: "warning",
    danger: "danger",
    info: "info"
};

export const getSelectBoxType = ({
    type
}: SelectBoxTypeConstantType): FieldType => {
    return SELECT_BOX_TYPES[type];
};

export const getIsSameSelectBoxSelection = ({
    current,
    next
}: SelectBoxSelectionConstantType): boolean => {
    if(current.length !== next.length) {
        return false;
    }

    return current.every((item, index) => item.__key === next[index]?.__key);
};

const syntheticItems = new WeakSet<object>();

export const getIsSelectBoxSyntheticItem = (item: object): boolean => syntheticItems.has(item);

export const getSelectBoxPreparedData = <T>({
    titleExtractor,
    selectedItems,
    keyExtractor,
    items
}: SelectBoxPreparedDataConstantType<T>): Array<T & SelectedItem | SelectedItem> => {
    const missingSelectedItems: Array<SelectedItem> = [];

    (selectedItems ?? []).forEach((selectedItem) => {
        const dataIndex = items.findIndex((item, index) => keyExtractor(item, index) === selectedItem.__key);

        if(dataIndex === -1) {
            syntheticItems.add(selectedItem);

            missingSelectedItems.unshift(selectedItem);
        }
    });

    const preparedItems = items.map((item, index) => {
        return {
            ...item,
            __title: titleExtractor(item, index),
            __key: keyExtractor(item, index)
        };
    });

    return [
        ...missingSelectedItems,
        ...preparedItems
    ];
};

export const getSelectBoxMappedSelection = ({
    selectedItems,
    data
}: SelectBoxMappedSelectionConstantType): Array<SelectedItem> => {
    return selectedItems.map((selectedItem) => {
        const dataItem = data.find((item) => item.__key === selectedItem.__key);

        return {
            ...selectedItem,
            __title: dataItem ? dataItem.__title : selectedItem.__title,
            __key: dataItem ? dataItem.__key : selectedItem.__key
        };
    });
};

const stylesheet = StyleSheet.create({
    readOnlyInput: {
        backgroundColor: "transparent",
        textAlignVertical: "center",
        includeFontPadding: false,
        alignSelf: "stretch",
        borderWidth: 0,
        minWidth: 0,
        padding: 0,
        margin: 0,
        flex: 1
    },
    trigger: {
        flexDirection: "row",
        alignItems: "center",
        alignSelf: "stretch",
        minWidth: 0,
        flex: 1,
        ...webStyle({
            outlineStyle: "solid",
            cursor: "pointer",
            outlineWidth: 0
        })
    },
    loadingTrigger: {
        ...webStyle({
            cursor: "not-allowed"
        })
    },
    chevron: {
        justifyContent: "center",
        alignItems: "center"
    },
    icon: {
        justifyContent: "center",
        alignItems: "center"
    },
    interactiveChevron: {
        ...webStyle({
            cursor: "pointer"
        })
    },
    value: {
        flexShrink: 1
    }
});

export const useStyles = ({
    isInteractive,
    currentSize,
    isLoading
}: SelectBoxDynamicStyleType) => {
    const styles = {
        icon: {
            minWidth: currentSize.iconSize,
            height: currentSize.lineHeight
        } as Mutable<ViewStyle>,
        chevron: {
            height: currentSize.iconSize,
            width: currentSize.iconSize
        } as Mutable<ViewStyle>,
        trigger: {
        } as Mutable<ViewStyle>
    };

    if(isInteractive) {
        Object.assign(styles.chevron, stylesheet.interactiveChevron);
    }

    if(isLoading) {
        Object.assign(styles.trigger, stylesheet.loadingTrigger);
    }

    return styles;
};
export default stylesheet;
